import { StoredRequest } from '@core/utils/store/stored-request-decorator';
import {
  EngineeringBrowseFilter,
  EngineeringSOFTabData,
  EngineeringSummaryTabData,
  EngineeringWorkflowTabData,
  EngineeringWorkflowUpdateData,
  PaginatedResponse,
  ProjectDetailsResponse,
  SiteAuditBrowseResponse,
  UpdateEngineeringSOFData,
} from '@models/interfaces';
import { BreadcrumbsActions, EngineeringActions } from '@store/actions';
import { ProjectFuzeIDsEffects, ProjectsEffects } from '@store/effects';
import { CommonHttpService } from './common-http.service';

export class EngineeringHttpService extends CommonHttpService {
  @StoredRequest(
    EngineeringActions.getEngineeringSummaryAction,
    BreadcrumbsActions.Engineering.engineering,
  )
  async getEngineeringSummary(
    projectBundleID: string,
  ): Promise<EngineeringSummaryTabData> {
    const response: EngineeringSummaryTabData = await this.http.callApi.get(
      `${this.apiUrl}/engineering/${projectBundleID}/summary`,
    );

    return ProjectsEffects.setHoldOptions(
      ProjectsEffects.setCurrentProvidenceAgent(
        ProjectsEffects.setPaceTasks(
          ProjectFuzeIDsEffects.setProjectFuzeIDsFromDetails(
            response as ProjectDetailsResponse,
          ),
        ),
      ),
    ) as EngineeringSummaryTabData;
  }

  @StoredRequest(EngineeringActions.getEngineeringWorkflowAction)
  async getEngineeringWorkflow(
    projectBundleID: string,
  ): Promise<EngineeringWorkflowTabData> {
    const response: EngineeringWorkflowTabData = await this.http.callApi.get(
      `${this.apiUrl}/engineering/${projectBundleID}/workflow`,
    );

    return ProjectsEffects.setPaceTaskFields(
      response as ProjectDetailsResponse,
    ) as EngineeringWorkflowTabData;
  }

  @StoredRequest(EngineeringActions.getEngineeringSOFAction)
  async getEngineeringSOF(
    projectBundleID: string,
  ): Promise<EngineeringSOFTabData> {
    const response: EngineeringSOFTabData = await this.http.callApi.get(
      `${this.apiUrl}/engineering/${projectBundleID}/sof`,
    );
    const { paceTaskFields: _paceTaskFields, ...SOFData } = response;

    return SOFData as EngineeringSOFTabData;
  }

  @StoredRequest(EngineeringActions.updateEngineeringWorkflowAction)
  async updateEngineering(
    id: string,
    data: EngineeringWorkflowUpdateData,
  ): Promise<EngineeringWorkflowUpdateData> {
    await this.http.callApi.patch(`${this.apiUrl}/engineering/workflow`, data, {
      params: { id },
    });

    return data;
  }

  @StoredRequest(EngineeringActions.updateEngineeringSOFAction)
  async updateSOF(
    id: string,
    data: UpdateEngineeringSOFData,
  ): Promise<UpdateEngineeringSOFData> {
    await this.http.callApi.patch(`${this.apiUrl}/engineering/sof`, data, {
      params: { id },
    });

    return data;
  }

  @StoredRequest(EngineeringActions.getEngineeringBrowseDataAction)
  getEngineeringBrowse(
    params: EngineeringBrowseFilter,
  ): Promise<PaginatedResponse<SiteAuditBrowseResponse[]>> {
    return this.http.callApi.get(`${this.apiUrl}/engineering`, null, {
      params,
    });
  }
}
